import { useMemo } from 'react';
import { useGetCustomerQuery } from '../api';
import { getTariffById, INITIAL_TARIFF, Tariff } from '../config/tariffs';
import { useTranslation } from './useTranslation';

export type TariffType = 'not_set' | 'unknown' | 'determined';

export interface UseTariffReturn {
    tariff: Tariff;
    tariffType: TariffType;
    isLoading: boolean;
}

/**
 * Хук для получения тарифа клиента
 * @returns Объект с тарифом из конфига и месячной ценой (из API или из конфига)
 */
export const useTariff = (): UseTariffReturn => {
    const { data: customer, isLoading } = useGetCustomerQuery();
    const { t } = useTranslation();

    const { tariff, tariffType } = useMemo((): { tariff: Tariff; tariffType: TariffType } => {
        if (!customer?.subscription) {
            return { tariff: INITIAL_TARIFF, tariffType: 'not_set' };
        }

        if (customer.subscription.id === 0) {
            return {
                tariff: {
                    ...INITIAL_TARIFF,
                    name: t.pages.payment.noTariff,
                },
                tariffType: 'not_set',
            };
        }

        const baseTariff = getTariffById(customer.subscription.id);
        if (!baseTariff) {
            return {
                tariff: {
                    ...INITIAL_TARIFF,
                    id: customer.subscription.id,
                    name: customer.subscription.name,
                    price: customer.subscription.price,
                    price_with_vat: customer.subscription.price_with_vat,
                },
                tariffType: 'unknown',
            };
        }

        return {
            tariff: {
                ...baseTariff,
                name: customer.subscription.name,
                price: customer.subscription.price,
                price_with_vat: customer.subscription.price_with_vat,
            },
            tariffType: 'determined',
        };
    }, [customer?.subscription?.id]);

    return {
        tariff,
        tariffType,
        isLoading,
    };
};
